import React from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import TokenIcon from '../components/TokenIcon';
import TransactionsList from '../components/TransactionsList';
import AddTransactionModal from '../AddTransactionModal';
import { useWalletsPage } from '../hooks/useWalletsPage';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { isRTL } from '../i18n';
import { useLocation } from 'react-router-dom';

function WalletTransactionsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    wallets,
    transactionFilters,
    handleFilterChange,
    uniqueCoins,
    loading,
    syncLoading,
    syncError,
    setSnackbar,
    transactions,
    transactionPagination,
    handleTransactionsPageChange,
    formatCurrency,
    prices,
    handleTransactionSort,
    transactionSort,
    realOnChainBalance,
    missingAmount,
    handleManualRefresh,
    setOpen,
    open,
    modalType,
    setModalType,
    editingTransaction,
    newTransaction,
    modalError,
    blockchains,
    blockchainsLoading,
    newWallet,
    setNewWallet,
    handleClose,
    handleSubmit,
    coinsLoading,
    handleAddManualHolding
  } = useWalletsPage();

  // On mount, set wallet filter from query string if present
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const walletId = params.get('walletId');
    console.log('[WalletTransactionsPage] Query walletId:', walletId);
    console.log('[WalletTransactionsPage] Wallets:', wallets);
    // Only set filter if wallets are loaded and walletId exists in wallets
    const walletExists = walletId && wallets.length > 0 && wallets.some(w => String(w.id) === String(walletId));
    console.log('[WalletTransactionsPage] walletExists:', walletExists, 'transactionFilters.walletId:', transactionFilters.walletId);
    if (
      walletExists &&
      transactionFilters.walletId !== walletId
    ) {
      console.log('[WalletTransactionsPage] Setting wallet filter to:', walletId);
      handleFilterChange('walletId', walletId);
    }
    // eslint-disable-next-line
  }, [location.search, wallets]);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('pages.walletTransactions.title')}
      </Typography>
      <Box display="flex" gap={2} mb={2} alignItems="center">
        {isRTL() ? (
          <>
            {/* Wallet Filter */}
            <FormControl fullWidth>
              <InputLabel shrink>{t('walletTransactions.filterByWallet')}</InputLabel>
              <Select
                value={transactionFilters.walletId}
                label={t('walletTransactions.filterByWallet')}
                displayEmpty
                onChange={(e) => handleFilterChange('walletId', e.target.value)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>{t('walletTransactions.allPortfolio')}</em>;
                  }
                  const wallet = wallets.find(w => String(w.id) === String(selected));
                  return wallet ? (wallet.label || wallet.address) : selected;
                }}
              >
                <MenuItem value="">
                  <em>{t('walletTransactions.allPortfolio')}</em>
                </MenuItem>
                {wallets.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.label || w.address}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Coin Filter */}
            <FormControl fullWidth>
              <InputLabel shrink>{t('walletTransactions.filterByCoin')}</InputLabel>
              <Select
                value={transactionFilters.coin}
                label={t('walletTransactions.filterByCoin')}
                displayEmpty
                onChange={(e) => handleFilterChange('coin', e.target.value)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>{t('walletTransactions.allCoins')}</em>;
                  }
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>{t('walletTransactions.allCoins')}</em>
                </MenuItem>
                {uniqueCoins.map((coin, idx) => (
                  <MenuItem key={coin + '-' + idx} value={coin}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TokenIcon coinSymbol={coin} size={24} />
                      {coin}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            {/* Coin Filter */}
            <FormControl fullWidth>
              <InputLabel shrink>{t('walletTransactions.filterByCoin')}</InputLabel>
              <Select
                value={transactionFilters.coin}
                label={t('walletTransactions.filterByCoin')}
                displayEmpty
                onChange={(e) => handleFilterChange('coin', e.target.value)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>{t('walletTransactions.allCoins')}</em>;
                  }
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>{t('walletTransactions.allCoins')}</em>
                </MenuItem>
                {uniqueCoins.map((coin, idx) => (
                  <MenuItem key={coin + '-' + idx} value={coin}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TokenIcon coinSymbol={coin} size={24} />
                      {coin}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Wallet Filter */}
            <FormControl fullWidth>
              <InputLabel shrink>{t('walletTransactions.filterByWallet')}</InputLabel>
              <Select
                value={transactionFilters.walletId}
                label={t('walletTransactions.filterByWallet')}
                displayEmpty
                onChange={(e) => handleFilterChange('walletId', e.target.value)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>{t('walletTransactions.allPortfolio')}</em>;
                  }
                  const wallet = wallets.find(w => String(w.id) === String(selected));
                  return wallet ? (wallet.label || wallet.address) : selected;
                }}
              >
                <MenuItem value="">
                  <em>{t('walletTransactions.allPortfolio')}</em>
                </MenuItem>
                {wallets.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.label || w.address}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>
      <TransactionsList
        transactions={transactions}
        loading={syncLoading}
        error={syncError}
        currentPage={transactionPagination.currentPage}
        totalPages={transactionPagination.totalPages}
        itemsPerPage={transactionPagination.limit}
        totalCount={transactionPagination.totalCount}
        onPageChange={handleTransactionsPageChange}
        formatCurrency={formatCurrency}
        wallets={wallets}
        prices={prices}
        onSort={handleTransactionSort}
        sortConfig={transactionSort}
        realOnChainBalance={realOnChainBalance}
        missingAmount={missingAmount}
        showTitle={false}
        onAddHoldings={() => setOpen(true)}
      />
      
      {/* Add Holdings Modal */}
      <Dialog open={open} onClose={() => handleClose(true)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTransaction ? t('portfolio.editHoldings') : t('portfolio.addHoldings')}</DialogTitle>
        <Tabs
          value={modalType}
          onChange={(_, newValue) => setModalType(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('walletTransactions.addWallet')} value="wallet" />
          <Tab label={editingTransaction ? t('walletTransactions.editManualTransaction') : t('walletTransactions.addManualTransaction')} value="transaction" />
        </Tabs>
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {modalType === 'wallet' ? (
            <>
              {modalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {modalError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth required error={Boolean(modalError && !newWallet.blockchain)}>
                  <InputLabel>{t('common.blockchain')}</InputLabel>
                  <Select
                    value={newWallet.blockchain}
                    label={t('common.blockchain')}
                    onChange={(e) => setNewWallet(prev => ({ ...prev, blockchain: e.target.value }))}
                    disabled={blockchainsLoading}
                  >
                    {blockchains.map((chain, index) => (
                      <MenuItem 
                        key={chain.symbol || `blockchain-${index}`} 
                        value={(chain.symbol || '').toLowerCase()}
                        disabled={!chain.is_supported}
                        sx={{
                          opacity: chain.is_supported ? 1 : 0.5,
                          color: chain.is_supported ? 'inherit' : 'text.disabled'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={chain.icon_url} 
                            alt={chain.name} 
                            style={{ 
                              width: 24, 
                              height: 24,
                              opacity: chain.is_supported ? 1 : 0.5
                            }} 
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{chain.name}</span>
                            {!chain.is_supported && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}>
                                Coming Soon
                              </span>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('common.address')}
                  value={newWallet.address}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(modalError && !newWallet.address)}
                  helperText={modalError && !newWallet.address ? t('common.enterAddress') : ''}
                />
                <TextField
                  label={t('common.walletLabel')}
                  value={newWallet.label}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, label: e.target.value }))}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={() => handleClose(true)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" sx={{ minWidth: 100 }}>
                  {t('common.addWallet')}
                </Button>
              </Box>
            </>
          ) : (
            <AddTransactionModal
              formOnly={true}
              onClose={() => handleClose(true)}
              onSubmit={handleAddManualHolding}
              loading={coinsLoading}
              initialValues={newTransaction}
              error={modalError}
              submitButtonText={editingTransaction ? t('walletTransactions.updateTransaction') : t('walletTransactions.addTransaction')}
              editing={!!editingTransaction}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default WalletTransactionsPage; 